import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import type { PaginatedResponse } from "@/types/api";

/** ISO ``YYYY-MM-DD`` dates in the upcoming window where the active
 *  location accepts reservations. Used by the staff /reservas page to
 *  pick a sensible default for ``selectedDate`` (the next open day) when
 *  today happens to be a closed weekday or full-day closure.
 *
 *  Mirrors the public ``/api/public/locations/available-dates/`` endpoint
 *  but lives behind staff auth. ``days`` is capped at 31 server-side. */
export function useAvailableDates(days = 14) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const locationId = useAuthStore((s) => s.locationId);
  return useQuery({
    queryKey: ["available-dates", locationId, days],
    queryFn: async () => {
      const params = new URLSearchParams({ days: String(days) });
      const res = await api
        .get(`locations/${locationId}/available-dates/?${params}`)
        .json<PaginatedResponse<string>>();
      return res.items;
    },
    enabled: isAuthenticated && !!locationId,
    staleTime: 5 * 60_000,
  });
}
